import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, AudioLines, Calendar, FileText, Headphones, Pause, Play, X } from 'lucide-react';
import { BlogPost, episodeNumbers, formatPostDate, isPodcast, postPath, useBlogPosts } from '../api/blog';
import PodcastPlayer, { AudioPlayer, useAudioPlayer } from '../components/PodcastPlayer';
import { PAGES, usePageMeta } from '../seo/seo';

const BlogPage = () => {
  const navigate = useNavigate();
  usePageMeta(PAGES.blogs);
  const { posts, status, reload } = useBlogPosts();
  const player = useAudioPlayer();

  const podcasts = posts.filter(isPodcast);
  const articles = posts.filter((post) => !isPodcast(post));
  const episodes = episodeNumbers(posts);
  const [featured, ...otherEpisodes] = podcasts;
  const nowPlaying = podcasts.find((post) => post.content.audio_url === player.src);

  const handleContactButtonClick = () => {
    navigate('/contact');
  };

  const handleEpisodePlay = (post: BlogPost) => {
    if (player.src === post.content.audio_url) player.toggle();
    else player.play(post.content.audio_url!);
  };

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Hero */}
      <section className="bg-gradient-to-r from-brandPurple to-brandBlue text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-white/80 mb-4">Blog & Podcast</p>
          <h1 className="text-4xl md:text-5xl font-bold max-w-4xl mx-auto leading-tight">
            Insights on AI-Powered Excel
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            Conversations on AI, automation and getting more out of Excel for finance and healthcare professionals.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 md:px-8 py-12 md:py-16">
        {status === 'loading' && <LoadingState />}

        {status === 'error' && (
          <div className="text-center py-16">
            <AlertCircle className="w-10 h-10 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">We couldn’t load the episodes</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">Please check your connection and try again.</p>
            <button
              type="button"
              onClick={reload}
              className="px-6 py-3 bg-[#3399ff] text-white rounded-lg hover:bg-opacity-90 transition-all duration-300"
            >
              Try again
            </button>
          </div>
        )}

        {status === 'ready' && posts.length === 0 && (
          <p className="text-center py-16 text-gray-600 dark:text-gray-300">New episodes and articles are coming soon.</p>
        )}

        {status === 'ready' && featured && (
          <>
            {/* Latest episode */}
            <section className="mb-16">
              <SectionHeading icon={<Headphones />} title="Latest Episode" />
              <div className="grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                <Link to={postPath(featured)} state={{ post: featured }} className="block bg-gray-100 dark:bg-gray-700">
                  <img
                    src={featured.content.image_url}
                    alt=""
                    className="w-full h-full max-h-80 md:max-h-none object-cover"
                  />
                </Link>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <EpisodeMeta post={featured} episode={episodes.get(featured.id)} />
                  <Link
                    to={postPath(featured)}
                    state={{ post: featured }}
                    className="mt-2 text-2xl md:text-3xl font-bold text-gray-900 dark:text-white hover:text-brandPurple dark:hover:text-brandBlue transition-colors"
                  >
                    {featured.title}
                  </Link>
                  <div className="mt-6">
                    <PodcastPlayer player={player} audioUrl={featured.content.audio_url!} />
                  </div>
                </div>
              </div>
            </section>

            {/* All episodes */}
            {otherEpisodes.length > 0 && (
              <section className="mb-16">
                <SectionHeading icon={<AudioLines />} title="More Episodes" />
                <ul className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                  {otherEpisodes.map((post) => (
                    <EpisodeRow
                      key={post.id}
                      post={post}
                      episode={episodes.get(post.id)}
                      player={player}
                      onPlay={() => handleEpisodePlay(post)}
                    />
                  ))}
                </ul>
              </section>
            )}
          </>
        )}

        {status === 'ready' && articles.length > 0 && (
          <section className="mb-16">
            <SectionHeading icon={<FileText />} title="Articles" />
            <div className="grid gap-8 md:grid-cols-2">
              {articles.map((post) => (
                <ArticleCard key={post.id} post={post} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Call to action */}
      <section className="py-16 bg-[#3399ff]">
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Have a question we haven’t covered?</h2>
          <p className="text-lg text-white mb-8">Tell us about your Excel workflows and we’ll show you what’s possible.</p>
          <button
            type="button"
            onClick={handleContactButtonClick}
            className="px-8 py-3 bg-white text-[#3399FF] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300"
          >
            Get in Touch
          </button>
        </div>
      </section>

      {/* Mini player: sticks to the bottom of the screen while an episode is loaded */}
      {nowPlaying && (
        <div className="sticky bottom-0 z-40 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 flex items-center gap-3 md:gap-4">
            <img src={nowPlaying.content.image_url} alt="" className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
            <div className="min-w-0 w-28 sm:w-48 md:w-64 flex-shrink-0">
              <p className="text-xs text-gray-500 dark:text-gray-400">Now playing</p>
              <Link
                to={postPath(nowPlaying)}
                state={{ post: nowPlaying }}
                className="block text-sm font-semibold text-gray-900 dark:text-white truncate hover:text-brandPurple dark:hover:text-brandBlue"
              >
                {nowPlaying.title}
              </Link>
            </div>
            <div className="flex-1 min-w-0">
              <PodcastPlayer player={player} audioUrl={nowPlaying.content.audio_url!} compact />
            </div>
            <button
              type="button"
              onClick={player.stop}
              className="p-2 rounded-full text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
              aria-label="Close player"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionHeading = ({ icon, title }: { icon: React.ReactElement<{ size?: number }>; title: string }) => (
  <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white mb-6">
    <span className="text-[#3399FF] dark:text-[#36AC98]">{React.cloneElement(icon, { size: 24 })}</span>
    {title}
  </h2>
);

const EpisodeMeta = ({ post, episode }: { post: BlogPost; episode?: number }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
    {episode && (
      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#3399FF]/10 text-[#3399FF]">
        Episode {episode}
      </span>
    )}
    {formatPostDate(post) && (
      <span className="flex items-center gap-1">
        <Calendar className="w-4 h-4" /> {formatPostDate(post)}
      </span>
    )}
  </div>
);

interface EpisodeRowProps {
  post: BlogPost;
  episode?: number;
  player: AudioPlayer;
  onPlay: () => void;
}

const EpisodeRow: React.FC<EpisodeRowProps> = ({ post, episode, player, onPlay }) => {
  const isCurrent = player.src === post.content.audio_url;
  const isPlaying = isCurrent && player.isPlaying;

  return (
    <li className={`flex items-center gap-4 py-4 px-2 md:px-4 transition-colors ${isCurrent ? 'bg-[#3399FF]/5' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
      <button
        type="button"
        onClick={onPlay}
        className="group relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden"
        aria-label={`${isPlaying ? 'Pause' : 'Play'} ${post.title}`}
      >
        <img src={post.content.image_url} alt="" className="w-full h-full object-cover" />
        <span className={`absolute inset-0 flex items-center justify-center bg-black/40 text-white transition-opacity ${isCurrent ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'}`}>
          {isPlaying ? <Pause className="w-6 h-6" fill="currentColor" /> : <Play className="w-6 h-6 ml-0.5" fill="currentColor" />}
        </span>
      </button>
      <div className="flex-1 min-w-0">
        <EpisodeMeta post={post} episode={episode} />
        <Link
          to={postPath(post)}
          state={{ post }}
          className="mt-1 block font-semibold text-gray-900 dark:text-white hover:text-brandPurple dark:hover:text-brandBlue transition-colors"
        >
          {post.title}
        </Link>
      </div>
      <Link
        to={postPath(post)}
        state={{ post }}
        className="hidden sm:flex items-center gap-1 text-sm font-medium text-[#3399FF] hover:text-brandPurple transition-colors flex-shrink-0"
      >
        Details <ArrowRight className="w-4 h-4" />
      </Link>
    </li>
  );
};

const ArticleCard = ({ post }: { post: BlogPost }) => (
  <Link
    to={postPath(post)}
    state={{ post }}
    className="group flex flex-col bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-shadow duration-300"
  >
    <div className="aspect-video overflow-hidden bg-gray-100 dark:bg-gray-700">
      <img
        src={post.content.image_url}
        alt=""
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
    </div>
    <div className="p-5 flex-1">
      <p className="text-sm text-gray-500 dark:text-gray-400">{formatPostDate(post)}</p>
      <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white group-hover:text-brandPurple dark:group-hover:text-brandBlue transition-colors">
        {post.title}
      </h3>
      {post.content.blog_text && (
        <p className="mt-2 text-gray-600 dark:text-gray-300 line-clamp-3">{post.content.blog_text}</p>
      )}
    </div>
  </Link>
);

const LoadingState = () => (
  <div className="animate-pulse" aria-label="Loading episodes">
    <div className="h-7 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
    <div className="h-72 bg-gray-200 dark:bg-gray-800 rounded-2xl mb-16" />
    {[0, 1, 2].map((i) => (
      <div key={i} className="flex items-center gap-4 py-4">
        <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    ))}
  </div>
);

export default BlogPage;
